import { htmlSafe } from '@ember/template';
import { on } from '@ember/modifier';
import { tracked } from '@glimmer/tracking';
import Component from '@glimmer/component';
import type { TOC } from '@ember/component/template-only';
import type Owner from '@ember/owner';
import { PhCaretDown, PhCaretUp } from 'ember-phosphor-icons';

export type ListItem = {
  heading: string;
  body: string;
  ordinal?: number;
};

export interface StackedListItemSignature {
  Args: {
    // item: ListItem;
    isExpandable: boolean;
  };
  Element: HTMLUListElement;
  Blocks: {
    header: [];
    body: [];
  };
}

export interface StackedListSignature {
  Args: {
    items: ListItem[];
    isExpandable: boolean;
    hideDividers?: boolean;
  };
  Element: HTMLUListElement;
}

class StackedListItem extends Component<StackedListItemSignature> {
  @tracked showContent = false;

  constructor(owner: Owner, args: StackedListItemSignature['Args']) {
    super(owner, args);
    this.showContent = args.isExpandable ? false : true;
  }

  toggleContent = () => {
    this.showContent = !this.showContent;
  };

  <template>
    <li class="py-4">
      <div>
        <div class="tio-row-between [&_a]:tio-copy">
          {{yield to="header"}}
          {{#if @isExpandable}}
            <button
              type="button"
              {{on "click" this.toggleContent}}
              class="tio-row-center w-9 h-9 rounded-full bg-tio-gray-100"
            >
              {{#if this.showContent}}
                <PhCaretUp @size="24" class="tio-fg-muted" />
              {{else}}
                <PhCaretDown @size="24" class="tio-fg-muted" />
              {{/if}}
            </button>
          {{/if}}
        </div>
        {{#if this.showContent}}
          <div
            class="mt-1 mr-10 text-sm tio-fg-muted text-balance leading-relaxed tracking-wide [&_a]:tio-copy"
          >
            {{yield to="body"}}
          </div>
        {{/if}}
      </div>
    </li>
  </template>
}

const StackedList: TOC<StackedListSignature> = <template>
  <ul role="list" class="{{unless @hideDividers 'divide-y divide-gray-200'}}" ...attributes>
    {{#each @items as |item|}}
      <StackedListItem @isExpandable={{@isExpandable}} class="not:first:border-t">
        <:header>
          {{htmlSafe item.heading}}
        </:header>
        <:body>
          {{htmlSafe item.body}}
        </:body>
      </StackedListItem>
    {{/each}}
  </ul>
</template>;

export default StackedList;
